/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useEffect } from 'react'

import styled from 'styled-components'

import illustrationResult from '../../../assets/illustration-4.png'
import { useThemeContext } from '../../../contexts/ThemeContext'
import { useWeb3Context } from '../../../contexts/Web3Context'
import { pxToEm, pxToVw } from '../../../functions/units'
import { useCouponAmount } from '../../../hooks/useCouponAmount'
import Spinner from '../../Common/Spinner'

type Props = {
  receivedCoupon: number
}

const DonateResult = ({ receivedCoupon }: Props) => {
  const { account } = useWeb3Context()
  const { colors } = useThemeContext()

  const { couponAmount, refetch: fetchLatestCouponAmount } = useCouponAmount()

  useEffect(() => {
    ;(async () => {
      await fetchLatestCouponAmount()
    })()
  }, [fetchLatestCouponAmount])

  return (
    <StyledContainer>
      <StyledArticle>
        <img src={illustrationResult} alt="" />
        <h1>Your donation has been successfully completed.</h1>
        <p>
          Thanks for your donation! Our system received your transfer, thus then, {receivedCoupon}{' '}
          STAMPs were sent to your wallet with the address: <span>{account}</span>
        </p>
        <p>
          <strong>Your new STAMP balance is:</strong>{' '}
          <em>
            {couponAmount ? (
              `${couponAmount} STAMPs`
            ) : (
              <Spinner size={16} stroke={colors.primary} />
            )}
          </em>
        </p>
      </StyledArticle>
    </StyledContainer>
  )
}

export default DonateResult

const StyledContainer = styled.div`
  padding-inline: ${pxToVw(410)};
  padding-top: ${pxToVw(120)};
`

const StyledArticle = styled.article`
  margin-bottom: ${pxToVw(155)};
  text-align: center;

  > img {
    height: ${pxToVw(171)};
    object-fit: cover;
  }

  > h1 {
    color: ${({ theme }) => theme.colors.gray600};
    font-size: ${pxToVw(50)};
    font-weight: 500;
    margin-bottom: ${pxToVw(34)};
  }

  > p {
    color: ${({ theme }) => theme.colors.gray300};
    font-size: ${pxToVw(26)};
    line-height: ${pxToEm(34, 26)};

    &:not(:last-child) {
      margin-bottom: ${pxToVw(34)};
    }

    > span {
      color: ${({ theme }) => theme.colors.primary300};
    }
    > strong {
      color: ${({ theme }) => theme.colors.gray};
      font-weight: 500;
    }
  }
`
